import React, { useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";

import IconButton from "./IconButton";

const Dialog = ({
  actions,
  children,
  closeLabel = "Close dialog",
  closeOnBackdrop = true,
  description,
  open,
  onClose,
  size = "medium",
  title,
}) => {
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const descriptionId = description ? `${generatedId}-description` : undefined;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      returnFocusRef.current = document.activeElement;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
      if (returnFocusRef.current instanceof HTMLElement) {
        returnFocusRef.current.focus();
        returnFocusRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
  }, []);

  const requestClose = () => {
    onClose();
  };

  return (
    <dialog
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      className={`wsm-dialog wsm-dialog--${size}`}
      onCancel={(event) => { event.preventDefault(); requestClose(); }}
      onClick={(event) => { if (closeOnBackdrop && event.target === dialogRef.current) requestClose(); }}
      ref={dialogRef}
    >
      <div className="wsm-dialog__surface">
        <header className="wsm-dialog__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <IconButton aria-label={closeLabel} icon={X} onClick={requestClose} size="compact" />
        </header>
        <div className="wsm-dialog__body">{children}</div>
        {actions && <footer className="wsm-dialog__footer">{actions}</footer>}
      </div>
    </dialog>
  );
};

Dialog.propTypes = {
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
  closeLabel: PropTypes.string,
  closeOnBackdrop: PropTypes.bool,
  description: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  title: PropTypes.string.isRequired,
};

export default Dialog;
